import { ChangeDetectorRef, Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngrx/store';
import { EMPTY, interval, merge, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, expand, finalize, map, scan, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../shared/components/confirm-dialog/confirm-dialog.component';
import { confirmAndDiscardSet, confirmAndWithdraw } from '../drawing-withdraw';
import {
  AsBuiltStatus,
  DrawingExportJob,
  DrawingExportKind,
  DrawingIssueSet,
  DrawingSheet,
  DrawingSheetListParams,
  DrawingSheetListStatus,
} from '../../../../../../../../shared/models/drawing-sheet';
import { ProjectDrawingsService } from '../../../../../../../../shared/services/project-drawings.service';
import { DrawingUploadDialogComponent } from '../drawing-upload-dialog/drawing-upload-dialog.component';
import { DrawingExportDialogComponent } from '../drawing-export-dialog/drawing-export-dialog.component';
import { SHEET_TYPES, SheetType, sheetTypeLabel } from '../../../../../../../../shared/helpers/sheet-types';
import { ProjectAlignment, projectAlignments, StationAt, stationAt, stationName } from '../../../../../../../../shared/helpers/alignment-stationing';
import { projectDetails } from '../../../../../../core/projects.selectors';
import { naturalCompare, parseSheetSearch } from '../drawing-register-search';
import { DrawingsAnalytics, errorProps } from '../drawings-analytics';
import { RecentSheet, RecentSheetsService } from '../../../../../../../../shared/services/recent-sheets.service';
import { AsBuiltFilters } from '../drawings-as-built/drawings-as-built.component';
import { MultiSectionFilterSection } from '../../../filters/multi-section-filter/multi-section-filter.component';
import { CommonService } from '../../../../../../../../shared/services/common.service';

export type RegisterList = 'sheets' | 'as-built' | 'pending';

/** Which list the register shows and the As-built tab's filters: in the URL, so Back from a sheet returns to them */
export interface RegisterListState extends AsBuiltFilters {
  list: RegisterList;
  /** W53: the Sheets list's filter ('' in disciplines is "No discipline"); empty arrays filter nothing */
  disciplines?: string[];
  statuses?: DrawingSheetListStatus[];
  types?: SheetType[];
  /** W57: the Sheets list's Group by ('none' is the default and stays out of the URL) */
  group?: RegisterGroupBy;
}

/** W57: the register's Group by view option */
export type RegisterGroupBy = 'none' | 'discipline' | 'type';
export const REGISTER_GROUP_BYS: RegisterGroupBy[] = ['none', 'discipline', 'type'];

/** W57: one group header ("Plan · 66"); its sheets load when it is expanded, the first page first, the rest on Show all */
export interface SheetGroup {
  key: string;
  label: string;
  count: number;
  rows: DrawingSheet[] | null;
  expanded: boolean;
  loading: boolean;
  /** Audit 1: a page of this group failed: the rows so far stay, with Retry for the rest */
  error?: boolean;
  /** Its last load was Show all (Retry carries on with the rest) */
  all?: boolean;
}

/** The register's Filter (N) sections (W53) */
type RegisterFilterKey = 'discipline' | 'status' | 'type';
const NO_DISCIPLINE_LABEL = 'No discipline';

const EXPORT_POLL_MS = 3000;

/** A group's Show all reads the rest in pages of this many (the API's maximum) */
const ALL_SHEETS_PAGE = 200;

/** Review M2: a grouped load: the first group's rows (key) or, before any counts, one row for them (key null) */
interface GroupedLoad {
  by: Exclude<RegisterGroupBy, 'none'>;
  key: string | null;
}

/** A list-view row: a group header, a group's Show all, or a sheet */
export type RegisterListItem = { group: SheetGroup } | { more: SheetGroup } | DrawingSheet;

/** What asked for a register load (W48) */
type LoadReason = 'search' | 'filter' | 'at_station' | 'other';

@Component({
  selector: 'app-drawings-register',
  templateUrl: './drawings-register.component.html',
  styleUrls: ['./drawings-register.component.scss'],
  standalone: false,
})
export class DrawingsRegisterComponent implements OnInit, OnDestroy {
  /** The app's toast (Figma 11893:79195): CommonService.notification, no action button */
  private readonly notify = inject(CommonService);
  @Input() projectId: string;
  @Input() canConfigure = false;
  @Input() canPublish = false;
  @Output() openSheet = new EventEmitter<string>();
  @Output() openSet = new EventEmitter<string>();
  /** As-built tab: Open on sheet, focusing that mark */
  @Output() openMark = new EventEmitter<{ revision: string; mark: string }>();
  /** The list and As-built filters to show (from the URL) */
  @Input() listState: Partial<RegisterListState> | null = null;
  @Output() listStateChange = new EventEmitter<RegisterListState>();

  readonly pageSize = 20;
  readonly displayedColumns = ['thumb', 'number', 'title', 'discipline', 'type', 'rev', 'status', 'station', 'actions'];
  readonly sheetTypes = SHEET_TYPES;
  readonly statusOptions = [{ value: 'current', label: 'Current' }, { value: 'unpublished', label: 'Not published' }];
  readonly typeLabel = sheetTypeLabel;

  rows: DrawingSheet[] = [];
  pendingSets: DrawingIssueSet[] = [];
  totalRecords = 0;
  pageIndex = 0;
  activeTab: RegisterList = 'sheets';
  /** The As-built tab's status filter (the waiting-for-review link sets open) */
  asBuiltStatus: AsBuiltStatus | '' = '';
  asBuiltSheet = '';
  asBuiltRevision = '';
  asBuiltQuery = '';
  /** Publishers: open as-built marks, from the sheets list until the As-built tab has counted them itself */
  asBuiltOpen = 0;
  /** The As-built tab's counts once it has loaded (all statuses) */
  asBuiltTotal: number | null = null;
  viewMode: 'grid' | 'list' = 'grid';
  search = new FormControl('');
  /**
   * W53: the Filter (N) selections, several per section. '' in disciplines is "No discipline"; a sheet type of `other`
   * also lists sheets with no type.
   */
  filterDisciplines: string[] = [];
  filterStatuses: DrawingSheetListStatus[] = [];
  filterTypes: SheetType[] = [];
  /** The disciplines to offer: the API's list for the project, else those of the sheets loaded so far ('' = none) */
  disciplines: string[] = [];
  private filterSectionsCache: { key: string; sections: MultiSectionFilterSection[] } | null = null;
  loading = false;
  /** Audit 1: the list failed to load: said in the register, with Retry (the toast still says so too) */
  loadError = false;
  /** An export is queued or building */
  exporting = false;
  /** The finished export's signed link */
  exportUrl: string | null = null;
  /** Why At my station could not find you (W68: Sheets here is a field feature, mobile only) */
  stationHint = '';
  /** The project's alignments (At my station shows with one or more) */
  alignments: ProjectAlignment[] = [];
  /**
   * At my station (spec §17 rule 4): your station on an alignment, worked out in the browser. Only the station goes to
   * the API, as `at_station` (R43: no alignment); the position itself is kept here only to re-station on another
   * alignment, and is forgotten when the filter is turned off.
   */
  atStation: { alignment: string; at: StationAt } | null = null;
  stationTakenAt: Date | null = null;
  locatingStation = false;
  /** The alignment chosen for At my station; '' is the nearest one */
  stationAlignment = '';
  private stationPosition: { lat: number; lon: number } | null = null;
  /** Each load() asks again; only the latest answer lands (a slow search can't overwrite a fast one) */
  private readonly loads = new Subject<{ qp: DrawingSheetListParams; complete: boolean; reason: LoadReason; grouped: GroupedLoad | null }>();
  /** W48: PostHog events (counts and flags only: never the search text or a position) */
  private readonly track = inject(DrawingsAnalytics);
  private exportKind: DrawingExportKind = 'current';
  private readonly onDestroy = new Subject<void>();

  private readonly store = inject(Store);
  private readonly recentSheets = inject(RecentSheetsService);
  /** W44: a station typed in the search box (`at_station`), shown as a chip */
  searchStation: { station: number; label: string } | null = null;
  /** W57: Group by (None, Discipline or Type); None pages through the API's order */
  groupBy: RegisterGroupBy = 'none';
  /** W57: the groups of the last answer (from the API's counts), when there are two or more to tell apart */
  groups: SheetGroup[] = [];
  /** The API's facet counts under the current filters (each leaves out its own filter) */
  private disciplineCounts: Record<string, number> = {};
  private sheetTypeCounts: Record<string, number> = {};
  /** A new answer drops the group rows still loading for the previous one */
  private readonly groupsReset = new Subject<void>();
  /** W44: the sheets this account opened last on this project */
  recent: RecentSheet[] = [];
  private listItemsKey = '';
  private listItemsCache: RegisterListItem[] = [];

  constructor(private api: ProjectDrawingsService, private dialog: MatDialog, private snack: MatSnackBar, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    const state = this.listState;
    this.filterDisciplines = [...(state?.disciplines ?? [])];
    this.filterStatuses = [...(state?.statuses ?? [])];
    this.filterTypes = [...(state?.types ?? [])];
    this.groupBy = REGISTER_GROUP_BYS.includes(state?.group as RegisterGroupBy) ? state.group : 'none';
    if (state?.list === 'as-built' || (state?.list === 'pending' && this.canConfigure)) {
      this.activeTab = state.list;
    }
    this.asBuiltStatus = state?.status || '';
    this.asBuiltSheet = state?.sheet || '';
    this.asBuiltRevision = state?.revision || '';
    this.asBuiltQuery = state?.q || '';
    this.store
      .select(projectDetails)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(project => {
        this.alignments = projectAlignments(project?.alignment_geometry);
        this.cdr.markForCheck();
      });
    this.loads
      .pipe(
        switchMap(({ qp, complete, reason, grouped }) =>
          this.api.listSheets(this.projectId, qp).pipe(
            map(page => ({ ...page, complete, reason, grouped })),
            catchError(e => {
              this.trackLoad(reason, null, e);
              this.loading = false;
              this.loadError = true;
              this.notify.notification('Could not load drawings. Try again.', 'error');
              this.cdr.markForCheck();
              return EMPTY;
            }),
          ),
        ),
        takeUntil(this.onDestroy),
      )
      .subscribe(({ data, totalRecords, asBuiltOpen, asBuiltTotal, complete, reason, grouped, disciplines, disciplineCounts, sheetTypeCounts }) => {
        if (this.canPublish && typeof asBuiltOpen === 'number') {
          this.asBuiltOpen = asBuiltOpen;
        }
        // The As-built tab's count before anyone opens it (project-wide, like the tab's own unfiltered counts)
        if (typeof asBuiltTotal === 'number') {
          this.asBuiltTotal = asBuiltTotal;
        }
        this.rows = data;
        this.totalRecords = totalRecords;
        this.loading = false;
        this.loadError = false;
        this.trackLoad(reason, this.totalRecords, null);
        this.disciplines = disciplines
          ? Array.from(new Set(disciplines)).sort()
          : Array.from(new Set([...this.disciplines, ...data.map(r => r.discipline || '')])).sort();
        // Review M4: a discipline from the URL the project no longer has would filter invisibly (no box to untick): drop it
        const stale = disciplines ? this.filterDisciplines.filter(d => !this.disciplines.includes(d)) : [];
        if (stale.length) {
          this.filterDisciplines = this.filterDisciplines.filter(d => this.disciplines.includes(d));
          this.pageIndex = 0;
          this.load('filter');
          this.emitListState();
        }
        // W57: the group headers come from the API's counts. Review N2: a group's request swaps the user's filter on its
        // facet for the group, so only that facet's counts (which leave out their own filter) still describe the user's
        // filters; the other facet keeps the counts of the last request made with the user's filters alone.
        if (grouped?.key !== null && grouped?.key !== undefined) {
          if (grouped.by === 'discipline') {
            this.disciplineCounts = disciplineCounts ?? {};
          } else {
            this.sheetTypeCounts = sheetTypeCounts ?? {};
          }
        } else {
          this.disciplineCounts = disciplineCounts ?? {};
          this.sheetTypeCounts = sheetTypeCounts ?? {};
        }
        // Review M6: a grouping the answer no longer offers (from the URL, or the filters narrowed) reads as None, and says so
        if (this.groupBy !== 'none' && !this.groupingSuspended && (disciplineCounts || sheetTypeCounts)
          && !this.groupOptions.some(o => o.value === this.groupBy)) {
          this.groupBy = 'none';
          this.emitListState();
        }
        if (grouped) {
          // Review M2: a grouped answer is a group's rows (or one row for the counts): the total is the groups' sum
          const by = this.groupBy === 'none' ? grouped.by : this.groupBy;
          const counts = by === 'discipline' ? this.disciplineCounts : this.sheetTypeCounts;
          const sum = this.groupKeys(by).reduce((n, k) => n + (counts[k] ?? 0), 0);
          this.totalRecords = sum || totalRecords;
        }
        this.buildGroups(grouped?.key !== undefined && grouped.key !== null ? { key: grouped.key, rows: data } : null);
        if (grouped && this.groups.length < 2) {
          // Fewer than two groups reads as None: the list page after all
          this.load(reason, true);
          return;
        }
        // Every sheet, unfiltered (I2): Recent drops sheets gone or out of reach, and takes renumbered ones' new names.
        // A page of the list only renames: a sheet not on it may still be there.
        if (complete) {
          this.recent = this.recentSheets.reconcile(this.projectId, data, data.length >= totalRecords);
        }
      });
    this.recent = this.recentSheets.list(this.projectId);
    this.load();
    this.loadPending();
    this.search.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), map((): LoadReason => 'search'))
      .pipe(takeUntil(this.onDestroy))
      .subscribe(reason => {
        this.pageIndex = 0;
        // A typed station replaces At my station: one station filter at a time
        const parsed = parseSheetSearch(this.search.value);
        if (parsed && 'station' in parsed && (this.atStation || this.locatingStation)) {
          this.clearStation();
        }
        this.load(reason);
      });
  }

  /** `reason` says what asked (W48: the search and At my station are reported when the answer lands) */
  load(reason: LoadReason = 'other', flat = false): void {
    this.loading = true;
    const base = this.listParams();
    const by = !flat && this.groupBy !== 'none' && !this.groupingSuspended ? this.groupBy : null;
    if (by) {
      // Review M2: grouped, the list page is never shown. With the first group known (the last answer's counts under
      // these filters) its rows are the one request, and their meta brings the counts; else one row just for them.
      const first = this.groupKeys(by)[0];
      const qp: DrawingSheetListParams = first === undefined
        ? { ...base, start: 0, total: 1 }
        : { ...base, ...this.groupFilter(by, first), start: 0, total: this.pageSize };
      this.loads.next({ qp, complete: false, reason, grouped: { by, key: first ?? null } });
      return;
    }
    const qp: DrawingSheetListParams = { ...base, start: this.pageIndex * this.pageSize, total: this.pageSize };
    const complete = !qp.q && !qp.discipline && !qp.status && !qp.sheet_type && qp.at_station === undefined;
    this.loads.next({ qp, complete, reason, grouped: null });
  }

  /** The list's filter for one group */
  private groupFilter(by: Exclude<RegisterGroupBy, 'none'>, key: string): DrawingSheetListParams {
    return by === 'discipline' ? { discipline: [key] } : { sheet_type: [key as SheetType] };
  }

  /** The list's search and filters, without paging: the page and every group fetch use them */
  private listParams(): DrawingSheetListParams {
    const qp: DrawingSheetListParams = {};
    // W44: a station typed in the search runs the station filter (R43), shown as a chip; anything else is text
    const parsed = parseSheetSearch(this.search.value);
    this.searchStation = parsed && 'station' in parsed ? parsed : null;
    if (parsed && 'q' in parsed) {
      qp.q = parsed.q;
    }
    // W53: several values per filter, as arrays (discipline[]=…); a section with every option ticked is no filter
    const active = this.activeFilters;
    if (active.discipline.length) {
      qp.discipline = active.discipline;
    }
    if (active.status.length) {
      qp.status = active.status;
    }
    if (active.type.length) {
      qp.sheet_type = active.type;
    }
    if (this.searchStation) {
      qp.at_station = this.searchStation.station;
    } else if (this.atStation) {
      qp.at_station = Math.max(0, Math.round(this.atStation.at.station * 100) / 100);
    }
    return qp;
  }

  loadPending(): void {
    if (!this.canConfigure) {
      return;
    }
    this.api
      .listSets(this.projectId, 'draft')
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: sets => {
          this.pendingSets = sets;
        },
        error: e => this.fail(e),
      });
  }

  setTab(tab: RegisterList): void {
    this.activeTab = tab;
    this.emitListState();
  }

  /**
   * At my station: the sheets whose station range covers your station on the project alignment (the nearest one, or
   * the one chosen). The position never leaves the browser; only the station is sent.
   */
  toggleAtStation(): void {
    if (this.atStation || this.locatingStation) {
      this.clearStation();
      this.pageIndex = 0;
      this.load();
      return;
    }
    this.clearTypedStation();
    this.locatingStation = true;
    this.locate(
      () => this.locatingStation,
      p => {
        this.locatingStation = false;
        this.stationPosition = { lat: p.coords.latitude, lon: p.coords.longitude };
        this.stationTakenAt = new Date();
        this.applyStation();
      },
      () => {
        this.locatingStation = false;
        this.track.track('at_my_station_used', { outcome: 'no_fix', results: 0 });
      },
    );
  }

  /** Another alignment for At my station ('' is the nearest): re-stations the same position */
  chooseStationAlignment(name: string): void {
    this.stationAlignment = name;
    this.applyStation();
  }

  /** "STA 1018+50 on FBPTR Baseline (B-4), 25 ft right of it" */
  atStationText(): string {
    if (!this.atStation) {
      return '';
    }
    const { alignment, at } = this.atStation;
    const on = alignment ? ` on ${alignment}` : '';
    if (at.beyond) {
      const end = at.beyond === 'start' ? 'start' : 'end';
      return `You are ${this.feetText(at.offset_ft)} beyond the ${end} of the alignment${on}. Showing the sheets at its ${end}, STA ${stationName(at.station)}`;
    }
    const side = at.side && at.offset_ft >= 1 ? `, ${this.feetText(at.offset_ft)} ${at.side === 'L' ? 'left' : 'right'} of it` : '';
    return `Showing the sheets at STA ${stationName(at.station)}${on}${side}`;
  }

  private feetText(ft: number): string {
    return `${Math.round(ft).toLocaleString('en-US')} ft`;
  }

  private applyStation(): void {
    const position = this.stationPosition;
    if (!position) {
      return;
    }
    const chosen = this.alignments.filter(a => a.name === this.stationAlignment);
    let best: { alignment: string; at: StationAt } | null = null;
    for (const a of chosen.length ? chosen : this.alignments) {
      const at = stationAt(a.alignment, position);
      if (!best || at.offset_ft < best.at.offset_ft) {
        best = { alignment: a.name, at };
      }
    }
    if (!best) {
      this.clearStation();
      this.stationHint = 'This project has no alignment to station you on.';
      this.track.track('at_my_station_used', { outcome: 'no_alignment', results: 0 });
      return;
    }
    this.atStation = best;
    this.pageIndex = 0;
    this.load('at_station');
  }

  private clearStation(): void {
    this.atStation = null;
    this.stationPosition = null;
    this.stationTakenAt = null;
    this.locatingStation = false;
    this.stationHint = '';
  }

  /** One browser position for At my station; `waiting()` false means it was cancelled meanwhile */
  private locate(waiting: () => boolean, found: (p: GeolocationPosition) => void, failed: () => void): void {
    const geolocation = typeof navigator !== 'undefined' ? navigator.geolocation : undefined;
    if (!geolocation) {
      failed();
      this.stationHint = 'This browser cannot share its location.';
      return;
    }
    if (typeof window !== 'undefined' && window.isSecureContext === false) {
      failed();
      this.stationHint = 'Location needs a secure (https) connection to this site.';
      return;
    }
    this.stationHint = '';
    geolocation.getCurrentPosition(
      p => {
        if (!waiting()) {
          return;
        }
        found(p);
        this.cdr.markForCheck();
      },
      e => {
        if (!waiting()) {
          return;
        }
        failed();
        this.stationHint = e.code === e.PERMISSION_DENIED
          ? 'Location is off for this site. Allow it in the browser to find the sheets where you are.'
          : 'Your location could not be found. Try again.';
        this.cdr.markForCheck();
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 10000 },
    );
  }

  /** The search's × and the station chip's ×: back to the full register */
  clearSearch(): void {
    this.search.setValue('');
  }

  /** One station at a time (W44 I1): At my station replaces a station typed in the search */
  private clearTypedStation(): void {
    const parsed = parseSheetSearch(this.search.value);
    if (parsed && 'station' in parsed) {
      this.search.setValue('');
      this.searchStation = null;
    }
  }

  /** W57: grouped when Group by is on, the answer has its own order (I4) not, and there are two groups or more */
  get grouped(): boolean {
    return this.groupBy !== 'none' && !this.groupingSuspended && this.groups.length > 1;
  }

  /** Why grouping is off for now: a station lists the closest-fitting range first */
  get groupingSuspended(): string {
    if (this.searchStation || this.atStation) {
      return 'Sheets at a station list the closest-fitting range first';
    }
    return '';
  }

  /** W57: None always; Discipline and Type only when they would make two groups or more */
  /** The Group by pill's value ("Group by: None") */
  get groupByLabel(): string {
    return this.groupOptions.find(o => o.value === this.groupBy)?.label ?? 'None';
  }

  get groupOptions(): { value: RegisterGroupBy; label: string }[] {
    return [
      { value: 'none', label: 'None' },
      ...(this.groupKeys('discipline').length > 1 ? [{ value: 'discipline' as const, label: 'Discipline' }] : []),
      ...(this.groupKeys('type').length > 1 ? [{ value: 'type' as const, label: 'Type' }] : []),
    ];
  }

  /**
   * A grouping's keys under the current filters, in header order: disciplines alphabetical with "No discipline" last,
   * types in the SHEET_TYPES order. The API's counts leave out the facet's own filter, so a filter on that facet keeps
   * only the groups it chose.
   */
  private groupKeys(by: Exclude<RegisterGroupBy, 'none'>): string[] {
    const counts = by === 'discipline' ? this.disciplineCounts : this.sheetTypeCounts;
    const chosen: string[] = by === 'discipline' ? this.activeFilters.discipline : this.activeFilters.type;
    const keys = Object.keys(counts).filter(k => counts[k] > 0 && (!chosen.length || chosen.includes(k)));
    if (by === 'discipline') {
      return keys.sort((a, b) => (a === '' ? 1 : b === '' ? -1 : naturalCompare(a, b)));
    }
    const order: string[] = SHEET_TYPES.map(t => t.value);
    const rank = (k: string) => (order.includes(k) ? order.indexOf(k) : order.length);
    return keys.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  }

  /** The headers for the answer just in: all collapsed but the first, whose first page loads (one request per change) */
  /** `first`: the first group's rows when the answer already brought them (review M2) */
  private buildGroups(first: { key: string; rows: DrawingSheet[] } | null = null): void {
    this.groupsReset.next();
    this.listItemsKey = '';
    const by = this.groupBy;
    if (by === 'none' || this.groupingSuspended) {
      this.groups = [];
      return;
    }
    const counts = by === 'discipline' ? this.disciplineCounts : this.sheetTypeCounts;
    this.groups = this.groupKeys(by).map((key, i) => ({
      key, count: counts[key], rows: null, expanded: i === 0, loading: false,
      label: by === 'discipline' ? key || NO_DISCIPLINE_LABEL : sheetTypeLabel(key as SheetType),
    }));
    if (this.groups.length > 1) {
      if (first && this.groups[0].key === first.key) {
        this.groups[0].rows = first.rows;
      } else {
        this.loadGroup(this.groups[0]);
      }
    }
  }

  /** A group's sheets: its first page, or (Show all) the rest in pages of 200; the list's filters with the group's own */
  private loadGroup(group: SheetGroup, all = false): void {
    const by = this.groupBy;
    const loaded = group.rows?.length ?? 0;
    const size = all ? ALL_SHEETS_PAGE : this.pageSize;
    const qp: DrawingSheetListParams = { ...this.listParams(), ...this.groupFilter(by as Exclude<RegisterGroupBy, 'none'>, group.key) };
    // Each page carries its own start, so the next one starts after the rows actually loaded (review C1)
    const page = (start: number) => this.api.listSheets(this.projectId, { ...qp, start, total: size }).pipe(map(res => ({ res, start })));
    group.loading = true;
    group.error = false;
    group.all = all;
    page(loaded).pipe(
      expand(({ res, start }) => {
        const got = res.data?.length ?? 0;
        const next = start + got;
        // Stop on a short page, or once the group (or the API's total) is all here
        return all && got === size && next < res.totalRecords && next < group.count ? page(next) : EMPTY;
      }),
      scan((acc: DrawingSheet[], { res }) => {
        // A guard against a row seen twice (a sheet moving between pages while reading)
        const seen = new Set(acc.map(r => r.uuid));
        return [...acc, ...(res.data ?? []).filter(r => !seen.has(r.uuid))];
      }, group.rows ?? []),
      // Audit 1: each page shows as it arrives (no last()): a long Show all fills in instead of waiting for the end
      finalize(() => {
        group.loading = false;
        this.listItemsKey = '';
        this.cdr.markForCheck();
      }),
      takeUntil(merge(this.groupsReset, this.onDestroy)),
    ).subscribe({
      next: rows => {
        group.rows = rows;
        this.listItemsKey = '';
        this.cdr.markForCheck();
      },
      error: e => {
        group.error = true;
        this.fail(e);
      },
    });
  }

  /** Audit 1: a group's failed page: carry on from the rows already here */
  retryGroup(group: SheetGroup): void {
    this.loadGroup(group, !!group.all);
  }


  /** The sheets shown without headers: the page as loaded */
  get flatRows(): DrawingSheet[] {
    return this.rows;
  }

  /** The list view's rows: group headers, each followed by its sheets and Show all when expanded. One array per change,
   *  so the table only re-renders when a group's state or rows change. */
  get listItems(): RegisterListItem[] {
    if (!this.grouped) {
      return this.flatRows;
    }
    const key = this.groups.map(g => `${g.expanded ? '+' : '-'}${g.key}:${g.rows?.length ?? 'x'}:${g.loading ? 1 : 0}`).join('|');
    if (this.listItemsKey !== key) {
      this.listItemsKey = key;
      this.listItemsCache = [];
      for (const group of this.groups) {
        this.listItemsCache.push({ group });
        if (group.expanded) {
          this.listItemsCache.push(...(group.rows ?? []));
          if (this.canShowAll(group) || group.loading) {
            this.listItemsCache.push({ more: group });
          }
        }
      }
    }
    return this.listItemsCache;
  }

  readonly isGroupItem = (_: number, item: RegisterListItem): boolean => !!(item as { group?: unknown }).group;
  readonly isMoreItem = (_: number, item: RegisterListItem): boolean => !!(item as { more?: unknown }).more;
  readonly isSheetItem = (_: number, item: RegisterListItem): boolean => !(item as { group?: unknown }).group && !(item as { more?: unknown }).more;

  /** Show all N: the group has more sheets than it has loaded */
  canShowAll(group: SheetGroup): boolean {
    return !!group.rows && group.rows.length < group.count && !group.loading;
  }

  toggleGroup(group: SheetGroup): void {
    group.expanded = !group.expanded;
    this.listItemsKey = '';
    if (group.expanded && group.rows === null && !group.loading) {
      this.loadGroup(group);
    }
  }

  showAll(group: SheetGroup): void {
    if (this.canShowAll(group)) {
      this.loadGroup(group, true);
    }
  }

  /** W57: the Group by dropdown; kept in the URL (None is the default and is not written) */
  setGroupBy(value: RegisterGroupBy): void {
    if (value === this.groupBy || !REGISTER_GROUP_BYS.includes(value)) {
      return;
    }
    this.groupBy = value;
    this.track.track('register_group_changed', { group: value });
    this.pageIndex = 0;
    if (value === 'none') {
      // The rows held are a group's (review M2): the list page now
      this.groups = [];
      this.load();
    } else {
      this.buildGroups();
    }
    this.emitListState();
  }

  /** Another filter is narrowing the list as well */
  get otherFilters(): boolean {
    const active = this.activeFilters;
    return !!(this.search.value || active.discipline.length || active.status.length || active.type.length);
  }

  /**
   * W53: the Filter (N) popover's sections (the app's multi-section filter, as on the Issues tab): Discipline, Status and
   * Type, each a checkbox list with All. Built once per change of options or selection, so the popover keeps its state.
   */
  get filterSections(): MultiSectionFilterSection[] {
    const key = JSON.stringify([this.disciplines, this.filterDisciplines, this.filterStatuses, this.filterTypes]);
    if (this.filterSectionsCache?.key !== key) {
      const option = (value: string, label: string, selected: string[]) => ({ value, label, isChecked: selected.includes(value) });
      this.filterSectionsCache = {
        key,
        sections: [
          {
            key: 'discipline', label: 'Discipline', searchable: this.disciplines.length > 8,
            options: this.disciplines.map(d => option(d, d || NO_DISCIPLINE_LABEL, this.filterDisciplines)),
          },
          { key: 'status', label: 'Status', options: this.statusOptions.map(o => option(o.value, o.label, this.filterStatuses)) },
          { key: 'type', label: 'Type', options: this.sheetTypes.map(t => option(t.value, t.label, this.filterTypes)) },
        ],
      };
    }
    return this.filterSectionsCache.sections;
  }

  /**
   * The choices that actually narrow the list: a section with every option ticked ("All") is the same as none, so it
   * sends no parameter, stays out of the URL and is not reported as a filter.
   */
  get activeFilters(): { discipline: string[]; status: DrawingSheetListStatus[]; type: SheetType[] } {
    const narrowing = <T extends string>(chosen: T[], all: readonly string[]): T[] =>
      chosen.length && all.length && all.every(v => chosen.includes(v as T)) ? [] : [...chosen];
    return {
      discipline: narrowing(this.filterDisciplines, this.disciplines),
      status: narrowing(this.filterStatuses, this.statusOptions.map(o => o.value)),
      type: narrowing(this.filterTypes, this.sheetTypes.map(t => t.value)),
    };
  }

  /** A Filter (N) section changed: the list from its first page, the choice kept in the URL */
  onFilterChange(event: { key: string; values: string[] }): void {
    const values = event.values ?? [];
    switch (event.key as RegisterFilterKey) {
      case 'discipline': this.filterDisciplines = values; break;
      case 'status': this.filterStatuses = values as DrawingSheetListStatus[]; break;
      case 'type': this.filterTypes = values as SheetType[]; break;
      default: return;
    }
    const sections = Object.entries(this.activeFilters).filter(([, v]) => v.length).map(([k]) => k);
    this.track.track('register_filter_changed', { sections, count: sections.length });
    this.pageIndex = 0;
    this.load('filter');
    this.emitListState();
  }

  /** The waiting-for-review signal: the As-built tab, filtered to open (whatever sheet or search was set) */
  showWaiting(): void {
    this.asBuiltStatus = 'open';
    this.asBuiltSheet = '';
    this.asBuiltRevision = '';
    this.asBuiltQuery = '';
    this.setTab('as-built');
  }

  onAsBuiltFilters(f: AsBuiltFilters): void {
    this.asBuiltStatus = f.status;
    this.asBuiltSheet = f.sheet;
    this.asBuiltRevision = f.revision;
    this.asBuiltQuery = f.q;
    this.emitListState();
  }

  private emitListState(): void {
    // A section with every option ticked is no filter: it stays out of the URL
    const active = this.activeFilters;
    this.listStateChange.emit({
      list: this.activeTab, status: this.asBuiltStatus, sheet: this.asBuiltSheet, revision: this.asBuiltRevision, q: this.asBuiltQuery,
      disciplines: active.discipline, statuses: active.status, types: active.type, group: this.groupBy,
    });
  }

  onAsBuiltCounts(counts: Record<AsBuiltStatus, number>): void {
    this.asBuiltOpen = counts.open;
    this.asBuiltTotal = counts.open + counts.reviewed + counts.rejected + counts.incorporated;
  }

  /** "120+00 – 135+00"; an open end (R39) reads "begin" or "end"; '—' with neither */
  stationLabel(row: DrawingSheet): string {
    const fmt = (v: number) => `${Math.floor(v / 100)}+${String(Math.round(v % 100)).padStart(2, '0')}`;
    const has = (v: number | null | undefined) => v !== null && v !== undefined;
    if (!has(row.station_start) && !has(row.station_end)) {
      return '—';
    }
    return `${has(row.station_start) ? fmt(row.station_start) : 'begin'} – ${has(row.station_end) ? fmt(row.station_end) : 'end'}`;
  }

  /** Second card line: `{discipline}. {station}`, skipping whichever part is missing. */
  subtitle(row: DrawingSheet): string {
    const station = this.stationLabel(row);
    return [row.discipline, station === '—' ? '' : station].filter(Boolean).join('. ');
  }

  statusLabel(row: DrawingSheet): string {
    return row.current ? 'Current' : 'Not published';
  }

  /** Rev and status are mutually exclusive, so a card shows at most 2 chips and never needs a `…+N` overflow. */
  cardLabel(row: DrawingSheet): string {
    return [row.number, row.title, row.current ? `Rev ${row.current.label ?? '—'}` : null].filter(Boolean).join(', ');
  }

  inspectLabel(set: DrawingIssueSet): string {
    switch (set.inspect?.status) {
      case 'ready':
        return 'Ready to review';
      case 'failed':
        return `Failed: ${this.api.failureMessage(set.inspect.error ?? undefined)}`;
      default:
        return 'Inspecting…';
    }
  }

  canWithdraw(row: DrawingSheet): boolean {
    return this.canPublish && !!row.current;
  }

  /** The sheet ⋯'s Withdraw too (drawing-withdraw.ts); the register does not know placements, so no map line here */
  withdraw(row: DrawingSheet): void {
    if (!row.current) {
      return;
    }
    confirmAndWithdraw(this.dialog, this.api, this.projectId, { revisionUuid: row.current.uuid, number: row.number, label: row.current.label, placed: false })
      .subscribe({ next: () => this.load(), error: e => this.fail(e) });
  }

  remove(row: DrawingSheet): void {
    const model = new ConfirmDialogModel('Delete sheet', `Delete ${row.number} and all its revisions?`, 'Delete', 'Cancel', true);
    this.dialog
      .open(ConfirmDialogComponent, { data: model })
      .afterClosed()
      .subscribe(ok => {
        if (ok) {
          this.api.deleteSheet(this.projectId, row.uuid).subscribe({ next: () => this.load(), error: e => this.fail(e) });
        }
      });
  }

  /** Discard set from the Pending list's ⋯ */
  discardSet(set: DrawingIssueSet): void {
    confirmAndDiscardSet(this.dialog, this.api, this.projectId, { setUuid: set.uuid, name: set.name, pages: set.revision_count ?? 0 })
      .pipe(takeUntil(this.onDestroy))
      .subscribe({
        next: () => {
          this.track.track('set_discarded', { set_uuid: set.uuid, pages: set.revision_count ?? 0, source: 'pending_list' });
          this.notify.notification('Set discarded', 'success');
          this.loadPending();
        },
        error: e => this.fail(e),
      });
  }

  upload(): void {
    this.dialog
      .open(DrawingUploadDialogComponent, { data: { projectId: this.projectId }, panelClass: ['floating-dialog-box', 'drawing-upload-dialog'] })
      .afterClosed()
      .subscribe((set: DrawingIssueSet | null) => {
        if (set) {
          this.openSet.emit(set.uuid);
        }
      });
  }

  /** Export asks which set: the current set or the as-built record set. */
  chooseExport(): void {
    if (this.exporting) {
      return;
    }
    this.dialog
      .open(DrawingExportDialogComponent, { width: '520px', panelClass: ['floating-dialog-box', 'drawing-export-dialog'] })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy))
      .subscribe((kind?: DrawingExportKind | null) => {
        if (kind) {
          this.exportSet(kind);
        }
      });
  }

  /**
   * Queues the export and polls it: every current sheet as a PDF with shared redlines plus index.csv, and for the
   * as-built record set deviations.csv too. The API also emails the requester, so leaving the register only stops the
   * polling.
   */
  exportSet(kind: DrawingExportKind = 'current'): void {
    if (this.exporting) {
      return;
    }
    this.exporting = true;
    this.exportKind = kind;
    this.exportUrl = null;
    this.notify.notification(kind === 'as_built'
      ? 'Preparing the as-built record set. We will also email you the link.'
      : 'Preparing the drawings export. We will also email you the link.');
    this.api
      .requestExport(this.projectId, kind)
      .pipe(
        switchMap(job => interval(EXPORT_POLL_MS).pipe(switchMap(() => this.api.exportStatus(this.projectId, job.uuid)))),
        takeWhile(job => job.status === 'queued' || job.status === 'building', true),
        finalize(() => (this.exporting = false)),
        takeUntil(this.onDestroy),
      )
      .subscribe({ next: job => this.onExportStatus(job), error: e => this.fail(e) });
  }

  page(e: { pageIndex: number }): void {
    this.pageIndex = e.pageIndex;
    this.load();
  }

  trackRow(_: number, row: DrawingSheet): string {
    return row.uuid;
  }

  private onExportStatus(job: DrawingExportJob): void {
    const outcome = job.status === 'ready' && job.download_url ? 'ready'
      : job.status === 'failed' || job.status === 'ready' ? 'failed' : job.status === 'expired' ? 'expired' : null;
    if (outcome) {
      this.track.track('set_exported', { kind: this.exportKind, outcome });
    }
    if (job.status === 'ready' && job.download_url) {
      const url = job.download_url;
      this.exportUrl = url;
      // The one toast with an action (Download does something): the app's toast look, opened directly for its onAction
      this.snack.open('Drawings export ready', 'Download', { duration: 15000, verticalPosition: 'top', horizontalPosition: 'center', panelClass: ['success'] })
        .onAction().pipe(takeUntil(this.onDestroy)).subscribe(() => {
          window.open(url, '_blank', 'noopener');
        });
    } else if (job.status === 'failed' || (job.status === 'ready' && !job.download_url)) {
      this.notify.notification('The drawings export failed. Try again.', 'error');
    } else if (job.status === 'expired') {
      this.notify.notification('The export link expired. Export again.', 'error');
    }
  }

  /** Opens a sheet from the register; where the list came from is the source */
  open(uuid: string): void {
    const source = this.searchStation || this.search.value ? 'search' : this.atStation ? 'at_station' : 'register';
    this.track.track('sheet_opened', { sheet_uuid: uuid, source });
    this.openSheet.emit(uuid);
  }

  /** W48: what the search and At my station found; never the text or the position */
  private trackLoad(reason: LoadReason, results: number | null, e: unknown): void {
    if (reason === 'search') {
      if (results !== null) {
        const text = (this.search.value ?? '').trim();
        this.track.track('register_searched', {
          has_station_query: !!this.searchStation, query_length: text.length, results,
          discipline_filter: !!this.activeFilters.discipline.length, type_filter: !!this.activeFilters.type.length,
        });
      }
    } else if (reason === 'at_station') {
      const outcome = e ? 'error' : results ? 'found' : 'no_sheets';
      this.track.track('at_my_station_used', { outcome, results: results ?? 0 });
    }
    if (e) {
      this.track.track('error_shown', { surface: 'register', ...errorProps(e) });
    }
  }

  private fail(e: { error?: { code?: string } }): void {
    this.track.error(this.exporting ? 'export' : 'register', e);
    this.notify.notification(this.api.failureMessage(e?.error?.code, this.api.errorMessage(e)), 'error');
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }
}
